import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
  CircularProgress,
  TextField,
  Paper,
} from "@mui/material";
import {
  Today,
  ViewWeek,
  NavigateBefore,
  NavigateNext,
  Search,
  Schedule,
  Person,
  CalendarMonth,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { url } from "../constants/url";

const CalendarView = ({ userData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [view, setView] = useState("timeGridWeek");
  const [leadsData, setLeadsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarApi, setCalendarApi] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const UID = userData?._id;
  const CID = userData?.companyAdded[0]?.companyId;
  const userRole = userData?.role;

  /** THEME COLORS - Premium Dark Theme */
  const COLORS = {
    primary: "#050712", // Deep dark blue/black for active states
    secondary: "#8B5CF6", // Purple accent
    accent: "#10B981", // Emerald green
    background: theme.palette.mode === "light" ? "#f8fafc" : "#0f0f1a",
    cardBackground: theme.palette.mode === "light"
      ? "rgba(255,255,255,0.95)"
      : "rgba(15, 15, 26, 0.95)",
    border: theme.palette.mode === "light" ? "rgba(226, 232, 240, 0.8)" : "rgba(51, 65, 85, 0.6)",
    textPrimary: theme.palette.mode === "light" ? "#1e293b" : "#f8fafc",
    textSecondary: theme.palette.mode === "light" ? "#64748b" : "#cbd5e1",
  };

  /** Lead Colors - Premium Palette */
  const LEAD_COLORS = [
    "#10B981", // Emerald
    "#8B5CF6", // Purple
    "#F59E0B", // Amber
    "#06B6D4", // Cyan
    "#EC4899", // Pink
    "#84CC16", // Lime
    "#F97316", // Orange
    "#6366F1", // Indigo
    "#14B8A6", // Teal
    "#EF4444", // Red
  ];

  const STATUS_CONFIG = {
    'hot': { label: 'Hot', color: '#EF4444', bgColor: '#FEF2F2' },
    'warm': { label: 'Warm', color: '#F59E0B', bgColor: '#FFFBEB' },
    'cold': { label: 'Cold', color: '#06B6D4', bgColor: '#ECFEFF' },
    'meeting_scheduled': { label: 'Meeting', color: '#10B981', bgColor: '#ECFDF5' },
    'quotation_shared': { label: 'Quotation', color: '#8B5CF6', bgColor: '#F5F3FF' },
    'follow-up': { label: 'Follow Up', color: '#F97316', bgColor: '#FFF7ED' },
    'rescheduled': { label: 'Rescheduled', color: '#6366F1', bgColor: '#EEF2FF' },
    'quotation_approved': { label: 'Approved', color: '#059669', bgColor: '#ECFDF5' },
    'closed': { label: 'Closed', color: '#047857', bgColor: '#ECFDF5' },
    'dead': { label: 'Dead', color: '#6B7280', bgColor: '#F9FAFB' }
  };

  const getLeadColor = (index) => LEAD_COLORS[index % LEAD_COLORS.length];
  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.cold;

  const getStartOfDay = (date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const getNextPaymentDate = (lead) => {
    if (lead.quotations && lead.quotations.length > 0) {
      const latestQuotation = lead.quotations[lead.quotations.length - 1];
      if (latestQuotation.payments && latestQuotation.payments.length > 0) {
        const allPayments = latestQuotation.payments
          .filter(p => p.nextPaymentDate)
          .sort((a, b) => new Date(a.nextPaymentDate) - new Date(b.nextPaymentDate));

        const today = getStartOfDay(new Date());
        const nextPayment = allPayments.find(p => getStartOfDay(new Date(p.nextPaymentDate)) >= today);

        if (nextPayment) return new Date(nextPayment.nextPaymentDate);
        if (allPayments.length > 0) return new Date(allPayments[allPayments.length - 1].nextPaymentDate);
      }
      if (latestQuotation.nextPaymentDate) return new Date(latestQuotation.nextPaymentDate);
    }
    return null;
  };

  const getDisplayDateForLead = (lead) => {
    if (!lead) return null;
    switch (lead.status) {
      case "meeting_scheduled":
        return lead.meeting?.dateTime || lead.leadCallDate;
      case "rescheduled":
        return lead.rescheduledDate || lead.leadCallDate;
      case "token_paid":
      case "token_received":
      case "partially_paid":
      case "payment_overdue":
        const nextPaymentDate = getNextPaymentDate(lead);
        return nextPaymentDate || lead.leadCallDate;
      default:
        return lead.leadCallDate;
    }
  };

  /** Fetch Leads */
  const fetchLeadsForCalendar = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${url}/api/leads/fetch-leads?userId=${UID}&role=${userRole}&companyId=${CID}`
      );
      const data = await response.json();

      if (data.data) {
        setLeadsData(data.data);
      }
    } catch (err) {
      console.error("Calendar error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadsForCalendar();
  }, []);

  /** Handle calendar date change */
  const handleDatesSet = (dateInfo) => {
    setCurrentDate(dateInfo.start);
  };

  /** Group leads by date for sequential display */
  const getGroupedLeadsByDate = useMemo(() => {
    const grouped = {};
    leadsData
      .filter((lead) => {
        if (!searchQuery) return true;
        return (
          lead.clientName?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
          lead.contactNo?.includes(searchQuery)
        );
      })
      .forEach((lead, index) => {
        const displayDate = getDisplayDateForLead(lead);
        if (!displayDate) return;

        const date = new Date(displayDate);
        const dateKey = format(date, 'yyyy-MM-dd');

        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push({ lead, index, date });
      });
    return grouped;
  }, [leadsData, searchQuery]);

  /** Convert Leads -> Events with sequential display */
  const calendarEvents = useMemo(() => {
    const events = [];

    Object.entries(getGroupedLeadsByDate).forEach(([dateKey, leadsOnDate]) => {
      // Sort leads for consistent ordering (like a list)
      leadsOnDate.sort((a, b) => a.lead.clientName?.localeCompare(b.lead.clientName));

      leadsOnDate.forEach(({ lead, index, date }, eventIndex) => {
        const statusConfig = getStatusConfig(lead.status);
        const isRescheduled = lead.status === 'rescheduled';

        events.push({
          id: lead._id,
          title: lead.clientName,
          start: date,
          allDay: true, // 🚨 KEY: This stacks them as a list one-by-one
          extendedProps: {
            lead,
            status: statusConfig.label,
            statusColor: statusConfig.color,
            contactNo: lead.contactNo,
            isRescheduled,
          },
          backgroundColor: isRescheduled
            ? alpha("#6366F1", 0.95)
            : alpha(getLeadColor(index), 0.95),
          borderColor: isRescheduled ? "#6366F1" : getLeadColor(index),
          textColor: "#ffffff",
          classNames: ["premium-event", isRescheduled ? "rescheduled-event" : ""],
        });
      });
    });

    return events;
  }, [getGroupedLeadsByDate]);

  /** Handle event click */
  const handleEventClick = (clickInfo) => {
    const leadId = clickInfo.event.id;
    navigate(`/lead/${leadId}`);
  };

  /** Format date display based on view */
  const getFormattedDate = () => {
    if (view === 'timeGridDay') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  /** PREMIUM HEADER */
  const headerUI = (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 2, md: 4 },
        py: 3,
        borderRadius: 0,
        backdropFilter: "blur(20px)",
        background: COLORS.cardBackground,
        borderBottom: `1px solid ${COLORS.border}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 20,
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
      }}
    >
      {/* LEFT */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        {/* <Typography
          variant="h4"
          fontWeight={800}
          sx={{ 
            letterSpacing: -0.5,
            color: COLORS.textPrimary,
          }}
        >
          Calendar
        </Typography> */}

        {/* Current Date Display */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: alpha(COLORS.primary, 0.08),
          borderRadius: '12px',
          px: 2,
          py: 1,
          border: `1px solid ${COLORS.border}`
        }}>
          <CalendarMonth sx={{ color: COLORS.primary, fontSize: 20 }} />
          <Typography
            variant="h6"
            fontWeight={600}
            color={COLORS.textPrimary}
            sx={{ fontSize: '1rem' }}
          >
            {getFormattedDate()}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => calendarApi?.prev()}
            size="medium"
            sx={{
              color: COLORS.textSecondary,
              background: alpha(COLORS.primary, 0.08),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: alpha(COLORS.primary, 0.15),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.2)}`,
              },
            }}
          >
            <NavigateBefore />
          </IconButton>

          <IconButton
            onClick={() => calendarApi?.today()}
            size="medium"
            sx={{
              color: COLORS.textSecondary,
              background: alpha(COLORS.primary, 0.08),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: alpha(COLORS.primary, 0.15),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.2)}`,
              },
            }}
          >
            <Today />
          </IconButton>

          <IconButton
            onClick={() => calendarApi?.next()}
            size="medium"
            sx={{
              color: COLORS.textSecondary,
              background: alpha(COLORS.primary, 0.08),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: alpha(COLORS.primary, 0.15),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.2)}`,
              },
            }}
          >
            <NavigateNext />
          </IconButton>
        </Box>
      </Box>

      {/* RIGHT */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        <TextField
          size="small"
          placeholder="Search leads..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: COLORS.textSecondary }} />,
            sx: {
              borderRadius: '12px',
              background: alpha(COLORS.cardBackground, 0.8),
              backdropFilter: "blur(10px)",
              border: `1px solid ${COLORS.border}`,
              '&:hover': {
                borderColor: COLORS.primary,
              },
              transition: 'all 0.3s ease',
            }
          }}
          sx={{
            width: 280,
          }}
        />

        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, v) => v && (setView(v), calendarApi?.changeView(v))}
          size="small"
          sx={{
            background: alpha(COLORS.cardBackground, 0.8),
            backdropFilter: "blur(10px)",
            borderRadius: '12px',
            p: 0.5,
            '& .MuiToggleButton-root': {
              color: COLORS.textSecondary,
              border: 'none',
              borderRadius: '8px',
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              '&.Mui-selected': {
                background: COLORS.primary,
                color: 'white',
                boxShadow: `0 4px 14px ${alpha(COLORS.primary, 0.4)}`,
              },
              '&:hover': {
                background: alpha(COLORS.primary, 0.1),
                color: COLORS.textPrimary,
              },
              transition: 'all 0.3s ease',
            }
          }}
        >
          <ToggleButton value="timeGridWeek">
            <ViewWeek sx={{ mr: 1, fontSize: 18 }} />
            Week
          </ToggleButton>
          <ToggleButton value="timeGridDay">
            <Today sx={{ mr: 1, fontSize: 18 }} />
            Day
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Paper>
  );

  /** Loading */
  if (loading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: COLORS.background,
        }}
      >
        <CircularProgress
          size={50}
          sx={{ color: COLORS.primary }}
        />
      </Box>
    );

  /** PREMIUM CALENDAR CSS */
  const calendarCSS = `
    .fc {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      background: ${COLORS.background};
    }

    .fc-toolbar { display: none !important; }

    /* Day Headers */
    .fc .fc-col-header-cell {
      background: ${COLORS.cardBackground};
      backdrop-filter: blur(20px);
      border: 1px solid ${COLORS.border};
      padding: 16px 12px;
    }

    .fc .fc-col-header-cell-cushion {
      color: ${COLORS.textPrimary};
      font-weight: 700;
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Time Slots - Compact Height */
    .fc .fc-timegrid-slot {
      height: 60px !important;
      border-bottom: 1px solid ${alpha(COLORS.border, 0.3)};
      background: ${COLORS.cardBackground};
    }

    .fc .fc-timegrid-slot-label {
      color: ${COLORS.textSecondary};
      font-weight: 600;
      font-size: 0.75rem;
      padding: 8px 12px;
      border: none !important;
    }

    /* Hide half-hour slots */
    .fc .fc-timegrid-slot.fc-timegrid-slot-minor {
      border: none !important;
      display: none !important;
    }

    /* Premium Event Style - Sequential List Display */
    .fc-event {
      border-radius: 6px !important;
      border: none !important;
      padding: 6px 10px !important;
      margin: 4px 6px !important;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
      transition: all 0.23s cubic-bezier(0.4, 0, 0.2, 1) !important;
      cursor: pointer !important;
      display: block !important;
    }

    .fc-event:hover {
      transform: translateX(6px) !important;
      box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
      z-index: 10 !important;
    }

    /* 🚨 FULL PAGE WHITE GRID FIX */
    .fc {
      background: #ffffff !important;
      height: 100% !important;
    }

    .fc-view-harness {
      background: #ffffff !important;
      border-top: 1px solid ${COLORS.border};
    }

    /* All Day Slot - Expand to fill page */
    .fc .fc-timegrid-allday {
      background: #ffffff !important;
    }

    .fc .fc-timegrid-allday-frame {
      min-height: calc(100vh - 180px) !important; /* Forces the white area to keep full page */
      padding: 12px !important;
    }

    /* Day Column Headers */
    .fc .fc-col-header-cell {
      background: #f8fafc !important;
      padding: 12px 0 !important;
      border-bottom: 2px solid ${alpha(COLORS.primary, 0.1)} !important;
    }

    /* Hide the hourly slots but keep the structure */
    .fc-timegrid-slots, 
    .fc-timegrid-axis-frame,
    .fc-timegrid-divider {
      display: none !important;
    }

    /* Rescheduled Event Style */
    .rescheduled-event {
      background: linear-gradient(135deg, #6366F1, #8B5CF6) !important;
      border-left: 5px solid #4F46E5 !important;
    }

    /* Event Title Style */
    .fc-event-title {
      font-size: 0.9rem !important;
      font-weight: 700 !important;
      letter-spacing: -0.2px !important;
    }

    /* Scrollbar */
    .fc-scroller::-webkit-scrollbar {
      width: 8px;
    }
    .fc-scroller::-webkit-scrollbar-track {
      background: #f1f5f9;
    }
    .fc-scroller::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }
    .fc-scroller::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
  `;

  return (
    <Box sx={{ background: "#ffffff", minHeight: "100vh", mt: -3 }}>
      <style>{calendarCSS}</style>

      {headerUI}

      <Box sx={{ height: "calc(100vh - 88px)" }}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          events={calendarEvents}
          eventClick={handleEventClick}
          headerToolbar={false}
          ref={(ref) => ref && setCalendarApi(ref.getApi())}
          slotMinTime="07:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={true}
          eventOverlap={true}
          nowIndicator={false}
          dayMaxEvents={true}
          eventMaxStack={false}
          height="100%"
          eventDisplay="block"
          eventOrder="title"
          slotDuration="01:00:00"
          slotLabelFormat={{
            hour: 'numeric',
            hour12: true,
            meridiem: 'short'
          }}
          slotLabelInterval="01:00:00"
          datesSet={handleDatesSet}
          eventDidMount={(info) => {
            // Add colored left border based on event type
            info.el.style.borderLeftColor = info.event.borderColor;

            // Ensure pointer cursor
            info.el.style.cursor = 'pointer';
          }}
        />
      </Box>
    </Box>
  );
};

export default CalendarView;